import { type PropsWithChildren, createContext, useContext, useState } from 'react';
import { Theme } from '@mui/material/styles';

import { Contact, AssetVehicle, Booking } from '../../../logic/api';

export const customerLookUpStyles = {
  searchWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  closeButton: (theme: Theme) => ({
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  }),
  cardColumn: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '40%',
    wordBreak: 'break-word'
  },
  card: {
    marginBottom: 8,
    cursor: 'pointer'
  },
  innerCardContainer: {
    display: 'flex',
  },
  icon: {
    fontSize: 24,
    marginTop: 4,
    marginRight: 12,
  },
};

export interface ICustomerLookUpResult {
  leadContact: {
    title: Contact['title'];
    firstName: Contact['firstName'];
    lastName: Contact['lastName'];
    phoneMobile: Contact['phoneMobile'];
    accountNumber: Contact['accountNumber'];
    email: Contact['email'];
  };
  vehicle: {
    registration: AssetVehicle['registration'];
    make: AssetVehicle['make'];
    model: AssetVehicle['model'];
    colour: AssetVehicle['colour'];
  };
  vip: Booking['vip'];
  assistance: Booking['assistance'];
  paxCount: Booking['paxCount'];
}

export interface ICustomerLookUpContext {
  customerLastName?: string;
  setCustomerLastName: (lastName: string) => void;
  vehicleRegistration?: string;
  setVehicleRegistration: (registration: string) => void;
  customerLookUpSelection?: ICustomerLookUpResult;
  setCustomerLookUpSelection: (selection: ICustomerLookUpResult) => void;
  clearCustomerLookUpSelection: () => void;
  customerLookUpDialogIsOpen: boolean;
  setCustomerLookUpDialogIsOpen: (value: boolean) => void;
}

const defaultContext: ICustomerLookUpContext = {
  customerLookUpDialogIsOpen: false,
  setCustomerLastName: () => console.warn('Customer look up context not instantiated.'),
  setVehicleRegistration: () => console.warn('Customer look up context not instantiated.'),
  setCustomerLookUpSelection: () => console.warn('Customer look up context not instantiated.'),
  clearCustomerLookUpSelection: () => console.warn('Customer look up context not instantiated.'),
  setCustomerLookUpDialogIsOpen: () => console.warn('Customer look up context not instantiated.')
};

const customerLookUpContext = createContext<ICustomerLookUpContext>(defaultContext);

export const useCustomerLookUp = () => useContext(customerLookUpContext);

export const CustomerLookUpProvider = ({
  children,
}: PropsWithChildren) => {
  const [customerLastName, setCustomerLastName] = useState<string>();
  const [vehicleRegistration, setVehicleRegistration] = useState<string>();
  const [customerLookUpSelection, setCustomerLookUpSelection] = useState<ICustomerLookUpResult>();
  const [customerLookUpDialogIsOpen, setCustomerLookUpDialogIsOpen] = useState<boolean>(false);
  const { Provider } = customerLookUpContext;

  const value: ICustomerLookUpContext = {
    customerLastName,
    setCustomerLastName: (lastName: string) => setCustomerLastName(lastName),
    vehicleRegistration,
    setVehicleRegistration: (registration: string) => setVehicleRegistration(registration),
    customerLookUpSelection,
    setCustomerLookUpSelection: (selection: ICustomerLookUpResult) => setCustomerLookUpSelection(selection),
    clearCustomerLookUpSelection: () => setCustomerLookUpSelection(undefined),
    customerLookUpDialogIsOpen,
    setCustomerLookUpDialogIsOpen: (value: boolean) => setCustomerLookUpDialogIsOpen(value),
  };

  return (
    <Provider value={value}>
      {children}
    </Provider>
  )
}

interface IMockCustomerLookUpProvider {
  context?: Partial<ICustomerLookUpContext>;
}

export const MockCustomerLookUpProvider = ({
  children,
  context,
}: PropsWithChildren<IMockCustomerLookUpProvider>) => {
  const { Provider } = customerLookUpContext;

  return (
    <Provider value={{ ...defaultContext, ...context }}>
      {children}
    </Provider>
  )
}
